<template>
  <div class="p-4 sm:p-6">
    <form @submit.prevent="createTask" class="space-y-4">
      <div class="flex flex-col" :class="{ 'create-task-invalid': !titleValidation.isValid }">
        <label class="block text-sm font-medium text-gray-700 mb-2">Task Title *</label>
        <input v-model="form.title" type="text"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter task title" @input="clearTitleValidity" />
        <transition name="fade" mode="out-in">
          <p v-if="!titleValidation.isValid" class="text-xs text-red-600 mt-1">{{ titleValidation.msg }}</p>
        </transition>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea v-model="form.description" rows="4"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter task description"></textarea>
      </div>

      <!-- Checklist -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Checklist</label>
        <div class="flex gap-2 mb-2">
          <input v-model="newChecklistItem" type="text"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Add checklist item..." @keydown.enter.prevent="addChecklistItem" />
          <button type="button" @click="addChecklistItem"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
            Add
          </button>
        </div>
        <div v-if="form.checklist_items.length > 0" class="space-y-2">
          <div v-for="(item, index) in form.checklist_items" :key="index"
            class="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-sm">
            <span class="text-gray-400">□</span>
            <span class="flex-1 text-gray-700">{{ item.title }}</span>
            <button type="button" @click="removeChecklistItem(index)"
              class="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50" title="Remove">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Bucket, Status, Priority, Due Date, Estimated Time -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div class="flex flex-col" :class="{ 'create-task-invalid': !bucketValidation.isValid }">
          <label class="block text-sm font-medium text-gray-700 mb-2">Bucket *</label>
          <select v-model="form.column_id"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            @change="clearBucketValidity">
            <option value="">Select bucket...</option>
            <option v-for="column in columns" :key="column.id" :value="column.id">
              {{ column.name }}
            </option>
          </select>
          <transition name="fade" mode="out-in">
            <p v-if="!bucketValidation.isValid" class="text-xs text-red-600 mt-1">{{ bucketValidation.msg }}</p>
          </transition>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select v-model="form.status"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="paused">Paused</option>
            <option value="in_review">In Review</option>
            <option value="done">Done</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Priority</label>
          <select v-model="form.priority"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
          <input v-model="form.due_date" type="date"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
        </div>

        <div class="flex flex-col">
          <label class="block text-sm font-medium text-gray-700 mb-2">Estimated Time</label>
          <div class="flex gap-2 mb-2">
            <button type="button" @click="setEstimatedTimeType('time')"
              :class="[
                'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                form.estimated_time_type === 'time'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              ]">
              Time
            </button>
            <button type="button" @click="setEstimatedTimeType('days')"
              :class="[
                'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                form.estimated_time_type === 'days'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              ]">
              Days
            </button>
          </div>
          <div v-if="form.estimated_time_type === 'time'" class="flex gap-2">
            <div class="flex-1">
              <input v-model.number="form.estimated_hours" type="number" min="0" placeholder="Hours"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div class="flex-1">
              <input v-model.number="form.estimated_minutes_input" type="number" min="0" max="59" placeholder="Minutes"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
          </div>
          <div v-else class="flex gap-2">
            <input v-model.number="form.estimated_days" type="number" min="1" placeholder="e.g. 2"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
            <span class="flex items-center text-sm text-gray-500 flex-shrink-0">days</span>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col" :class="{ 'create-task-invalid': !assigneeValidation.isValid }">
          <label class="block text-sm font-medium text-gray-700 mb-2">Assignees *</label>
          <multiselect v-model="form.assignees" :options="boardMembers" :multiple="true" :close-on-select="false"
            :clear-on-select="false" placeholder="Select one or more assignees" label="name" track-by="id" class="w-full"
            >
            <template #option="{ option }">
              <div class="flex flex-col py-1">
                <span class="text-sm font-medium text-gray-900">{{ option.name }}</span>
                <span class="text-xs text-gray-500">{{ option.email }}</span>
              </div>
            </template>
            <template #noResult>
              <span class="text-sm text-gray-500">No members found.</span>
            </template>
          </multiselect>
          <transition name="fade" mode="out-in">
            <p v-if="!assigneeValidation.isValid" class="text-xs text-red-600 mt-1">{{ assigneeValidation.msg }}</p>
          </transition>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Viewers</label>
          <multiselect v-model="form.viewers" :options="boardMembers" :multiple="true" :close-on-select="false"
            placeholder="Select people to notify on status changes (optional)" label="name" track-by="id"
            class="w-full">
            <template #option="{ option }">
              <div class="flex flex-col py-1">
                <span class="text-sm font-medium text-gray-900">{{ option.name }}</span>
                <span class="text-xs text-gray-500">{{ option.email }}</span>
              </div>
            </template>
            <template #noResult>
              <span class="text-sm text-gray-500">No members found.</span>
            </template>
          </multiselect>
          <p class="text-xs text-gray-500 mt-1">Viewers will receive notifications when the task status changes.</p>
        </div>
      </div>

      <!-- Attachments (full width) -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
        <div class="space-y-2">
          <!-- File Input -->
          <div class="flex items-center gap-2">
            <input ref="fileInput" type="file" multiple @change="handleFileSelect" class="hidden"
              id="task-attachments" />
            <label for="task-attachments"
              class="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              <span>Select Files</span>
            </label>
          </div>
          <!-- Selected Files List -->
          <div v-if="selectedFiles.length > 0" class="space-y-2">
            <div v-for="(file, index) in selectedFiles" :key="index"
              class="flex items-center justify-between bg-gray-50 rounded-lg p-2 text-sm">
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <svg class="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z">
                  </path>
                </svg>
                <span class="text-gray-700 truncate">{{ file.name }}</span>
                <span class="text-gray-500 text-xs">({{ formatFileSize(file.size) }})</span>
              </div>
              <button @click="removeFile(index)" class="text-red-500 hover:text-red-700 ml-2" type="button">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12">
                  </path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row gap-3 pt-4">
        <button type="button" @click="$emit('close')"
          class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button type="submit" :disabled="loading"
          class="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <span v-if="!loading">Create Task</span>
          <span v-else>Creating...</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

export default {
  name: 'create-task-modal',
  components: {
    Multiselect,
  },
  props: {
    boardId: {
      type: Number,
      required: true,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    boardMembers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      selectedFiles: [],
      titleValidation: { isValid: true, msg: '' },
      assigneeValidation: { isValid: true, msg: '' },
      bucketValidation: { isValid: true, msg: '' },
      newChecklistItem: '',
      form: {
        board_id: this.boardId,
        column_id: '',
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        due_date: '',
        assignees: [],
        viewers: [],
        checklist_items: [],
        estimated_time_type: 'time',
        estimated_hours: 0,
        estimated_minutes_input: 0,
        estimated_days: '',
      },
    };
  },
  watch: {
    'form.assignees'() {
      this.clearAssigneeValidity();
    },
    boardMembers: {
      handler(members) {
        if (members && members.length > 0 && (!this.form.assignees || this.form.assignees.length === 0)) {
          const currentUserId = this.$store.getters.userId;
          if (currentUserId) {
            const self = members.find(m => m.id && parseInt(m.id) === parseInt(currentUserId));
            if (self) {
              this.form.assignees = [self];
            }
          }
        }
      },
      immediate: true,
    },
    boardId(newId) {
      this.form.board_id = newId;
    },
  },
  methods: {
    getDefaultColumn(cols) {
      const columns = cols || this.columns;
      return columns.find(c => (c.name && c.name.toLowerCase() === 'to do') || c.order === 0) || columns[0];
    },
    getDefaultColumnId(cols) {
      const col = this.getDefaultColumn(cols);
      return col ? col.id : (this.columns.length > 0 ? this.columns[0].id : '');
    },
    setEstimatedTimeType(type) {
      if (this.form.estimated_time_type === type) return;
      this.form.estimated_time_type = type;
      if (type === 'time') {
        this.form.estimated_hours = 0;
        this.form.estimated_minutes_input = 0;
      } else {
        this.form.estimated_days = '';
      }
    },
    getComputedEstimatedMinutes() {
      if (this.form.estimated_time_type === 'time') {
        const h = parseInt(this.form.estimated_hours, 10) || 0;
        const m = parseInt(this.form.estimated_minutes_input, 10) || 0;
        return h * 60 + m;
      }
      const d = parseInt(this.form.estimated_days, 10);
      if (isNaN(d) || d < 1) return null;
      return d * 24 * 60;
    },
    validateForm() {
      let isValid = true;
      this.titleValidation = { isValid: true, msg: '' };
      this.assigneeValidation = { isValid: true, msg: '' };
      this.bucketValidation = { isValid: true, msg: '' };

      if (!this.form.title || !this.form.title.trim()) {
        this.titleValidation = { isValid: false, msg: 'Task title is required.' };
        isValid = false;
      }
      if (!this.form.column_id) {
        this.bucketValidation = { isValid: false, msg: 'Please select a bucket.' };
        isValid = false;
      }
      if (!this.form.assignees || this.form.assignees.length === 0) {
        this.assigneeValidation = { isValid: false, msg: 'Please select at least one assignee.' };
        isValid = false;
      }
      return isValid;
    },
    clearTitleValidity() {
      this.titleValidation = { isValid: true, msg: '' };
    },
    clearAssigneeValidity() {
      this.assigneeValidation = { isValid: true, msg: '' };
    },
    clearBucketValidity() {
      this.bucketValidation = { isValid: true, msg: '' };
    },
    async createTask() {
      if (!this.validateForm()) {
        return;
      }
      this.loading = true;
      try {
        const input = {
          board_id: parseInt(this.form.board_id),
          column_id: parseInt(this.form.column_id),
          title: this.form.title,
          description: this.form.description || null,
          status: this.form.status || 'todo',
          priority: this.form.priority,
        };

        if (this.form.due_date) {
          input.due_date = this.form.due_date;
        }
        if (this.form.assignees && this.form.assignees.length > 0) {
          input.assignee_ids = this.form.assignees.map(function (a) {
            return parseInt(a.id, 10);
          });
        }
        if (this.form.viewers && this.form.viewers.length > 0) {
          input.viewer_ids = this.form.viewers.map(v => parseInt(v.id));
        }
        if (this.form.checklist_items && this.form.checklist_items.length > 0) {
          input.checklist_items = this.form.checklist_items.map(item => ({ title: item.title }));
        }
        const estMinutes = this.getComputedEstimatedMinutes();
        input.estimated_minutes = (estMinutes != null && estMinutes >= 1) ? estMinutes : null;
        input.estimated_time_type = this.form.estimated_time_type || 'time';

        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateTask($input: CreateTaskInput!) {
              createTask(input: $input) {
                error
                error_code
                message
                data {
                  id
                  title
                }
              }
            }
          `,
          variables: {
            input,
          },
        });

        if (data.createTask.error) {
          await this.$swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: data.createTask.message || 'Failed to create task.',
          });
        } else {
          const taskId = data.createTask.data.id;

          // Upload attachments if any
          if (this.selectedFiles.length > 0) {
            await this.uploadAttachments(taskId);
          }

          // Reset form
          this.form = {
            board_id: this.boardId,
            column_id: '',
            title: '',
            description: '',
            status: 'todo',
            priority: 'medium',
            due_date: '',
            assignees: [],
            viewers: [],
            checklist_items: [],
            estimated_time_type: 'time',
            estimated_hours: 0,
            estimated_minutes_input: 0,
            estimated_days: '',
          };
          this.newChecklistItem = '';
          this.selectedFiles = [];

          this.$emit('created');
        }
      } catch (error) {
        console.error('Error creating task:', error);
        await this.$swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Failed to create task. Please try again.',
        });
      } finally {
        this.loading = false;
      }
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.selectedFiles = [...this.selectedFiles, ...files];
      // Reset input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    addChecklistItem() {
      const title = (this.newChecklistItem || '').trim();
      if (!title) return;
      this.form.checklist_items.push({ title });
      this.newChecklistItem = '';
    },
    removeChecklistItem(index) {
      this.form.checklist_items.splice(index, 1);
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    async uploadAttachments(taskId) {
      for (const file of this.selectedFiles) {
        try {
          await this.$apollo.mutate({
            mutation: gql`
              mutation UploadTaskAttachment($task_id: ID!, $file: Upload!) {
                uploadTaskAttachment(task_id: $task_id, file: $file) {
                  error
                  error_code
                  message
                  data {
                    id
                    file_name
                  }
                }
              }
            `,
            variables: {
              task_id: taskId,
              file: file,
            },
          });
        } catch (error) {
          console.error('Error uploading attachment:', error);
          // Continue with other files even if one fails
        }
      }
      // Clear selected files after upload
      this.selectedFiles = [];
    },
  },
};
</script>

<style scoped>
.create-task-invalid label {
  @apply text-red-600;
}

.create-task-invalid input,
.create-task-invalid :deep(.multiselect) {
  border-color: #ef4444;
}

.create-task-invalid :deep(.multiselect__tags) {
  border-color: #ef4444;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
